import {
  TeamPreferencesResource,
  TeamResource,
  TeamSettings,
} from "@/types/resources";
import { api } from "@/utils/api";
import { createModel } from "@rematch/core";
import { RootModel } from ".";

interface TeamState {
  selectedChannels?: Array<number>;
}

interface State {
  [teamId: string]: TeamResource & TeamState;
}

const initialState: State = {};

export const teams = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setTeams(state, teams: TeamResource[]) {
      return teams.reduce((acc, curr) => {
        const teamId = curr.id.toString();
        return { ...acc, [teamId]: { ...(state[teamId] || {}), ...curr } };
      }, {});
    },
    setTeam(state, updatedTeam: TeamResource) {
      return {
        ...state,
        [updatedTeam.id.toString()]: updatedTeam,
      };
    },
    setUpdatedSettings(
      state,
      {
        updatedSettings,
        teamId,
      }: { teamId: string; updatedSettings: Partial<TeamSettings> }
    ) {
      return {
        ...state,
        [teamId]: {
          ...state[teamId],
          settings: {
            ...(state[teamId].settings || {}),
            ...updatedSettings,
          },
        },
      };
    },
    setTeamPreferences(
      state,
      {
        preferences,
        teamId,
      }: { teamId: string; preferences: TeamPreferencesResource }
    ) {
      return {
        ...state,
        [teamId]: {
          ...state[teamId],
          preferences,
        },
      };
    },
    setSelectedChannels(
      state,
      payload: { teamId: string; channels: Array<number> }
    ) {
      return {
        ...state,
        [payload.teamId]: {
          ...state[payload.teamId],
          selectedChannels: payload.channels,
        },
      };
    },
    updateTeamTestingProperties(
      state,
      payload: {
        properties: Pick<TeamResource, "testingChannels" & "testingSettings">;
        teamId: number;
      }
    ) {
      return {
        ...state,
        [payload.teamId]: {
          ...state[payload.teamId],
          ...payload.properties,
        },
      };
    },
  },
  effects: (dispatch) => ({
    async updateTeam(payload: { teamId: string | number; formData: FormData }) {
      const team = await api.post<FormData, TeamResource>(
        `/v1/teams/${payload.teamId}`,
        payload.formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      dispatch.teams.setTeam(team);
    },
    async updateSettings({
      teamId,
      updatedSettings,
    }: {
      teamId: string;
      updatedSettings: Partial<TeamSettings>;
    }) {
      await api.post<Partial<TeamSettings>, Partial<TeamSettings>>(
        `/v1/teams/${teamId}/settings`,
        updatedSettings
      );
      dispatch.teams.setUpdatedSettings({
        teamId,
        updatedSettings,
      });
    },
    async updateTeamPreferences({
      teamId,
      preferences,
    }: {
      teamId: string;
      preferences: TeamPreferencesResource;
    }) {
      const updatedPreferences = await api.post<
        TeamPreferencesResource,
        TeamPreferencesResource
      >(`/v1/teams/${teamId}/preferences`, preferences);

      dispatch.teams.setTeamPreferences({
        teamId,
        preferences: updatedPreferences,
      });
    },
  }),
});
